import { canWaterWidgetsShareSpace } from '../../../utils/waterNetworkLayout';

export const createCollisionHandlers = ({
	getEditableMetrics,
	getWidgetRectPx,
	toPxNumber,
	defaultDivWidth,
	defaultDivHeight,
}) => {
	const checkOverlap = (x, y, w, h, widgetsToCheck, currentWidgetId = null, subjectWidgetOrType = null) => {
		if (!Array.isArray(widgetsToCheck)) {
			return false;
		}

		const metrics = getEditableMetrics();
		const subjectWidget = subjectWidgetOrType
			|| (currentWidgetId
				? widgetsToCheck.find((widget) => widget?.id === currentWidgetId)
				: null);
		const newLeft = toPxNumber(x, 0);
		const newTop = toPxNumber(y, 0);
		const newWidth = toPxNumber(w, 0);
		const newHeight = toPxNumber(h, 0);
		const newRight = newLeft + newWidth;
		const newBottom = newTop + newHeight;

		for (const elem of widgetsToCheck) {
			if (currentWidgetId && elem.id === currentWidgetId) {
				continue;
			}
			if (elem.type === 'background-image' || elem.type === 'box-widget') {
				continue;
			}
			if (canWaterWidgetsShareSpace(subjectWidget, elem)) {
				continue;
			}

			const existingRectPx = getWidgetRectPx(elem, metrics);
			const existingLeft = existingRectPx.left;
			const existingTop = existingRectPx.top;
			const existingWidth = existingRectPx.width;
			const existingHeight = existingRectPx.height;
			const existingRight = existingLeft + existingWidth;
			const existingBottom = existingTop + existingHeight;

			if (
				newLeft < existingRight &&
				newRight > existingLeft &&
				newTop < existingBottom &&
				newBottom > existingTop
			) {
				return true;
			}
		}
		return false;
	};

	const checkIsOnBackgroundImage = (x, y, w, h, widgetsToCheck, currentWidgetId = null) => {
		if (!Array.isArray(widgetsToCheck)) {
			return false;
		}

		const metrics = getEditableMetrics();
		const newLeft = toPxNumber(x, 0);
		const newTop = toPxNumber(y, 0);
		const newWidth = toPxNumber(w, 0);
		const newHeight = toPxNumber(h, 0);
		const newRight = newLeft + newWidth;
		const newBottom = newTop + newHeight;

		for (const elem of widgetsToCheck) {
			if (currentWidgetId && elem.id === currentWidgetId) {
				continue;
			}
			if (elem.type !== 'background-image') {
				continue;
			}

			const existingRectPx = getWidgetRectPx(elem, metrics);
			const existingLeft = existingRectPx.left;
			const existingTop = existingRectPx.top;
			const existingWidth = existingRectPx.width;
			const existingHeight = existingRectPx.height;
			const existingRight = existingLeft + existingWidth;
			const existingBottom = existingTop + existingHeight;

			if (
				newLeft < existingRight &&
				newRight > existingLeft &&
				newTop < existingBottom &&
				newBottom > existingTop
			) {
				return true;
			}
		}
		return false;
	};

	const getContainingBackgroundImage = (x, y, widgetsToCheck, currentWidgetId = null) => {
		if (!Array.isArray(widgetsToCheck)) {
			return null;
		}

		const metrics = getEditableMetrics();
		const pointX = toPxNumber(x, 0);
		const pointY = toPxNumber(y, 0);

		for (const elem of widgetsToCheck) {
			if (currentWidgetId && elem.id === currentWidgetId) {
				continue;
			}
			if (elem.type !== 'background-image') {
				continue;
			}

			const backgroundRectPx = getWidgetRectPx(elem, metrics);
			const bgLeft = backgroundRectPx.left;
			const bgTop = backgroundRectPx.top;
			const bgWidth = backgroundRectPx.width;
			const bgHeight = backgroundRectPx.height;
			const bgRight = bgLeft + bgWidth;
			const bgBottom = bgTop + bgHeight;

			if (
				pointX >= bgLeft &&
				pointX <= bgRight &&
				pointY >= bgTop &&
				pointY <= bgBottom
			) {
				return elem;
			}
		}
		return null;
	};

	const getOverlappingBackgroundImage = (x, y, w, h, widgetsToCheck, currentWidgetId = null) => {
		if (!Array.isArray(widgetsToCheck)) {
			return null;
		}

		const metrics = getEditableMetrics();
		const newLeft = toPxNumber(x, 0);
		const newTop = toPxNumber(y, 0);
		const newWidth = toPxNumber(w, 0);
		const newHeight = toPxNumber(h, 0);
		const newRight = newLeft + newWidth;
		const newBottom = newTop + newHeight;

		let bestMatch = null;
		let bestOverlapArea = 0;

		for (const elem of widgetsToCheck) {
			if (currentWidgetId && elem.id === currentWidgetId) {
				continue;
			}
			if (elem.type !== 'background-image') {
				continue;
			}

			const backgroundRectPx = getWidgetRectPx(elem, metrics);
			const bgLeft = backgroundRectPx.left;
			const bgTop = backgroundRectPx.top;
			const bgRight = bgLeft + backgroundRectPx.width;
			const bgBottom = bgTop + backgroundRectPx.height;

			const overlapWidth = Math.max(0, Math.min(newRight, bgRight) - Math.max(newLeft, bgLeft));
			const overlapHeight = Math.max(0, Math.min(newBottom, bgBottom) - Math.max(newTop, bgTop));
			const overlapArea = overlapWidth * overlapHeight;

			if (overlapArea > bestOverlapArea) {
				bestOverlapArea = overlapArea;
				bestMatch = elem;
			}
		}

		return bestMatch;
	};

	const constrainRectToBackgroundImage = (x, y, w, h, widgetsToCheck, currentWidgetId = null) => {
		const rawLeft = toPxNumber(x, 0);
		const rawTop = toPxNumber(y, 0);
		const rawWidth = Math.max(1, toPxNumber(w, 1));
		const rawHeight = Math.max(1, toPxNumber(h, 1));
		const backgroundWidget = getContainingBackgroundImage(
			rawLeft,
			rawTop,
			widgetsToCheck,
			currentWidgetId,
		) || getOverlappingBackgroundImage(
			rawLeft,
			rawTop,
			rawWidth,
			rawHeight,
			widgetsToCheck,
			currentWidgetId,
		);

		if (!backgroundWidget) {
			return {
				left: rawLeft,
				top: rawTop,
				width: rawWidth,
				height: rawHeight,
				constrained: false,
			};
		}

		const metrics = getEditableMetrics();
		const backgroundRect = getWidgetRectPx(backgroundWidget, metrics);
		const maxWidth = Math.max(1, backgroundRect.width);
		const maxHeight = Math.max(1, backgroundRect.height);
		const constrainedWidth = Math.min(rawWidth, maxWidth);
		const constrainedHeight = Math.min(rawHeight, maxHeight);
		const constrainedLeft = Math.min(
			Math.max(rawLeft, backgroundRect.left),
			backgroundRect.left + backgroundRect.width - constrainedWidth,
		);
		const constrainedTop = Math.min(
			Math.max(rawTop, backgroundRect.top),
			backgroundRect.top + backgroundRect.height - constrainedHeight,
		);

		return {
			left: constrainedLeft,
			top: constrainedTop,
			width: constrainedWidth,
			height: constrainedHeight,
			constrained: true,
		};
	};

	return {
		checkIsOnBackgroundImage,
		checkOverlap,
		constrainRectToBackgroundImage,
		getContainingBackgroundImage,
		getOverlappingBackgroundImage,
	};
};
